import { PaletteColorOptions, Theme, createTheme } from "@mui/material/styles";
import * as colors from "./colors";

// MUI's light/dark palette mode. Defined here because @mui/material/styles does
// not re-export the `PaletteMode` type in this version.
export type PaletteMode = "light" | "dark";

export const DEFAULT_SPACING = 2;
export const SPACING_BASE_PIXELS = 8;

export * from "./colors";

type BorderPalette = {
  subtle: string;
  warm: string;
};

type StatusPalette = {
  active: string;
  scheduled: string;
  paused: string;
};

type VerdictPalette = {
  positive: string;
  negative: string;
  failed: string;
  running: string;
  rule: string;
  hairline: string;
  muted: string;
  soft: string;
};

declare module "@mui/material" {
  interface ButtonPropsVariantOverrides {
    primary: true;
    ghost: true;
    naked: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    olive: Palette["primary"];
    lightText: Palette["text"];
    border: BorderPalette;
    status: StatusPalette;
    verdict: VerdictPalette;
  }

  interface PaletteOptions {
    olive?: PaletteColorOptions;
    lightText?: Palette["text"];
    border?: BorderPalette;
    status?: StatusPalette;
    verdict?: VerdictPalette;
  }
}

export const olive = {
  light: colors.oliveLighter,
  extraLight: colors.oliveLightest,
  main: colors.olive,
  dark: colors.oliveDarker,
  contrastText: colors.white,
};

// Dark-mode primary. The muted olive reads as washed-out on dark surfaces, so
// dark uses the brighter Remerge yellow-green for higher-contrast action
// buttons (with black text). Same shape as `olive` so it slots into the palette.
const darkPrimary = {
  light: colors.yellowLighter,
  extraLight: colors.yellowLightest,
  main: colors.yellowDark,
  dark: colors.yellowDarker,
  contrastText: colors.black,
};

export const blackShades = {
  main: colors.black,
  light: colors.blackLightest,
  contrastText: colors.white,
};

const warningShades = {
  light: colors.warningLight,
  main: colors.warningMedium,
  dark: colors.warningDark,
};

const statusShades = {
  active: colors.successDark,
  scheduled: colors.indigo,
  paused: colors.lightGrayDarkest,
};

const errorShades = {
  light: colors.errorMedium,
  main: colors.errorDark,
};

export const monospaced = Object.freeze({
  fontFamily: "monospace",
});

const contentFont = "GT America Regular, Helvetica, Arial, sans-serif";
const headlineFont = "GT America Condensed Regular, Helvetica, Arial, sans-serif";

// Semantic tokens that differ between light and dark. Light values are exactly
// the originals so the light theme is unchanged; dark values keep the Remerge
// palette but dim the surfaces and lighten the text. Kept in one place so the
// palette and the component overrides below stay in sync.
type ModeTokens = {
  primary: typeof blackShades | typeof olive;
  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  borderSubtle: string;
  borderWarm: string;
  background?: { default: string; paper: string };
  appBarBackground: string;
  appBarColor: string;
  primaryButtonBackground: string;
  primaryButtonColor: string;
  primaryButtonHoverBackground: string;
  primaryButtonHoverColor: string;
  outlineHoverBackground: string;
  outlineHoverBorder: string;
  disabledButtonBackground: string;
  disabledButtonColor: string;
  verdict: VerdictPalette;
};

function tokensForMode(mode: PaletteMode): ModeTokens {
  if (mode === "dark") {
    return {
      // Brighter Remerge yellow-green drives primary actions (buttons, links,
      // focus) on dark so they read with strong contrast instead of washing out.
      primary: darkPrimary,
      textPrimary: colors.lightGray,
      textSecondary: colors.whiteDarker,
      textDisabled: colors.blackLightest,
      borderSubtle: colors.darkBorder,
      borderWarm: colors.darkBorderWarm,
      appBarBackground: colors.darkSurfaceRaised,
      appBarColor: colors.lightGray,
      primaryButtonBackground: colors.yellowDark,
      primaryButtonColor: colors.black,
      primaryButtonHoverBackground: colors.yellowLighter,
      primaryButtonHoverColor: colors.black,
      outlineHoverBackground: colors.darkSurfaceOverlay,
      outlineHoverBorder: colors.white,
      disabledButtonBackground: colors.darkSurfaceOverlay,
      disabledButtonColor: colors.whiteDarkest,

      background: {
        default: colors.darkSurface,
        paper: colors.darkSurfaceRaised,
      },

      verdict: {
        positive: colors.successMedium,
        negative: colors.errorMedium,
        failed: colors.warningMedium,
        running: colors.blueDeep,
        rule: colors.blackLightest,
        hairline: colors.darkBorder,
        muted: colors.whiteDarkest,
        soft: colors.whiteDarker,
      },
    };
  }

  return {
    primary: blackShades,
    textPrimary: colors.black,
    textSecondary: blackShades.light,
    textDisabled: colors.whiteDarker,
    borderSubtle: colors.lightGrayLighter,
    borderWarm: colors.cream,
    background: undefined, // keep MUI light defaults (white)
    appBarBackground: colors.lightGray,
    appBarColor: colors.black,
    primaryButtonBackground: colors.black,
    primaryButtonColor: colors.white,
    primaryButtonHoverBackground: colors.darkBackground,
    primaryButtonHoverColor: colors.lightGrayFont,
    outlineHoverBackground: colors.white,
    outlineHoverBorder: colors.black,
    disabledButtonBackground: colors.disabledGray,
    disabledButtonColor: colors.white,

    verdict: {
      positive: colors.verdictPositive,
      negative: colors.verdictNegative,
      failed: colors.verdictFailed,
      running: colors.verdictRunning,
      rule: colors.verdictRule,
      hairline: colors.verdictHairline,
      muted: colors.verdictMuted,
      soft: colors.verdictSoft,
    },
  };
}

/**
 * createAppTheme — build the shared MUI theme for a given light/dark mode.
 *
 * The light theme is identical to the original single theme. The dark theme
 * reuses the same structure and swaps in the dark tokens above, so components
 * that read the theme (styled/useTheme/sx) adapt automatically.
 */
export function createAppTheme(mode: PaletteMode = "light"): Theme {
  const t = tokensForMode(mode);

  return createTheme({
    palette: {
      mode,
      primary: t.primary,
      olive,
      warning: warningShades,
      error: errorShades,

      //info : {}
      ...(t.background ? { background: t.background } : {}),

      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
        disabled: t.textDisabled,
      },

      border: {
        subtle: t.borderSubtle,
        warm: t.borderWarm,
      },

      status: statusShades,

      verdict: t.verdict,

      lightText: {
        primary: colors.white,
        secondary: colors.whiteDarker,
        disabled: colors.whiteDark,
      },
    },

    typography: {
      fontFamily: contentFont,
      h1: { fontFamily: headlineFont, fontWeight: 100 },
      h2: { fontFamily: headlineFont, fontWeight: 100 },
      h3: { fontFamily: headlineFont, fontWeight: 100 },
      h4: { fontFamily: headlineFont, fontWeight: 100 },
      h5: { fontFamily: headlineFont, fontWeight: 100 },
      h6: { fontFamily: headlineFont, fontWeight: 100 },
    },

    components: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: t.appBarBackground,
            color: t.appBarColor,
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            margin: 16,
          },

          paperScrollPaper: {
            maxHeight: `calc(100% - ${16 * 2}px)`,
          },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: { margin: "16px 0" }, // Add the same style as in expanded mode
        },
      },

      MuiAlert: {
        styleOverrides: {
          standardWarning: {
            backgroundColor: `${warningShades.light}20`,
          },

          standardError: {
            backgroundColor: `${errorShades.light}20`,
          },
        },
      },

      MuiButton: {
        variants: [
          {
            props: { size: "large" },
            style: { fontSize: 18, height: 50 },
          },
          {
            props: { size: "medium" },
            style: { fontSize: 16, height: 40 },
          },
          {
            props: { size: "small" },
            style: { fontSize: 14, height: 30 },
          },
          {
            props: {
              variant: "primary",
            },

            style: {
              background: t.primaryButtonBackground,
              color: t.primaryButtonColor,

              "&:hover": {
                backgroundColor: t.primaryButtonHoverBackground,
                color: t.primaryButtonHoverColor,
              },
            },
          },
          {
            props: {
              variant: "naked",
            },

            style: {
              border: `1px solid ${colors.lightGrayFont}`,

              "&:hover": {
                backgroundColor: t.outlineHoverBackground,
                border: `1px solid ${t.outlineHoverBorder}`,
              },
            },
          },
          {
            props: {
              variant: "ghost",
            },

            style: {
              backgroundColor: "transparent",
              borderWidth: 0,
              border: "1px solid transparent",

              "&:hover": {
                backgroundColor: t.outlineHoverBackground,
                border: `1px solid ${t.outlineHoverBorder}`,
              },
            },
          },
          {
            props: {
              disabled: true,
            },

            style: {
              backgroundColor: t.disabledButtonBackground,
              borderWidth: 0,

              "&:disabled": {
                color: t.disabledButtonColor,
              },
            },
          },
        ],
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              "&.Mui-focused fieldset": {
                border: `1px solid`,
              },
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid",
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          // Light keeps MUI's default resting border; dark uses a stronger
          // border than MUI's faint default so form fields stay clearly outlined.
          notchedOutline: {
            borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.38)" : "rgba(0, 0, 0, 0.23)",
          },
        },
      },
    },
  });
}

export const lightTheme = createAppTheme("light");
export const darkTheme = createAppTheme("dark");

export const theme = lightTheme;

export default theme;
